// lib/leasing/run-narration.ts
//
// WHAT THE AGENT SAYS WHILE A SOURCING PASS RUNS.
//
// Extracted from the effect in SourcingCenterView for one reason: it is the only part of the
// narration that can be checked without spending credits. A real run bills one credit per brand,
// so the wording and the cadence would otherwise ship on nothing but a reading of the code.
//
// ONE line per run that updates in place with every scored brand. Quarter milestones were tried
// first and left a 50-brand pass silent for minutes, which read as a stalled run (2026-09-26).

/**
 * Why a pass stopped before it reached the end of its brand list. `BUDGET_EXHAUSTED` is the only
 * one so far: the orchestrator stops launching brands once the measured LLM spend reaches the
 * campaign's credit cap, which happens whenever a brand costs more than the one credit the slider
 * sells it for.
 */
export type SourcingTruncationReason = 'BUDGET_EXHAUSTED';

/**
 * The line itself, in the agent's voice. `total` of 0 means the run has been accepted but has not
 * reported a size yet - it still has to say something, or the launch looks unacknowledged.
 */
export const resolveRunNarrationLine = (
  completed: number,
  total: number,
  lang: string,
  inFlightNames = '',
): string => {
  const safeTotal = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  const safeCompleted = Number.isFinite(completed) ? Math.max(0, Math.floor(completed)) : 0;
  const isFrench = lang === 'fr';

  if (safeTotal === 0) {
    return isFrench ? 'Je lance la passe de sourcing' : 'Starting the sourcing pass';
  }
  if (safeCompleted === 0) {
    return isFrench
      ? `Je lance la passe sur ${safeTotal} marques`
      : `Starting the pass on ${safeTotal} brands`;
  }
  const scored = Math.min(safeCompleted, safeTotal);
  const count = isFrench ? `${scored} marques notees sur ${safeTotal}` : `Scored ${scored} of ${safeTotal} brands`;
  if (!inFlightNames || scored >= safeTotal) return count;
  return isFrench ? `${count} · réflexion sur ${inFlightNames}…` : `${count} · contemplating ${inFlightNames}…`;
};

/**
 * The line for a pass that has STOPPED, which is a different sentence from the running one.
 *
 * A run that spends its credit budget at brand 29 of 50 abandons the other 21 and is still stamped
 * COMPLETE. On screen that was indistinguishable from a finished run, so "Scored 29 of 50" read as
 * a counter that had frozen. Measured on campaign cmsuod81s000hxqt0f03afdag: 50 shortlist rows,
 * 29 with a score, 21 left PENDING, billed 50.0 credits against a 50 credit cap. The counter was
 * right and the run was short; saying so is the whole fix, because the failure it causes is a user
 * who waits, concludes it is stuck, and relaunches a run that bills again.
 *
 * Returns null when the pass genuinely covered its list, so the caller can stay silent rather than
 * announce a non-event.
 */
export const resolveRunTruncationLine = (
  completed: number,
  total: number,
  truncatedReason: SourcingTruncationReason | null | undefined,
  lang: string,
): string | null => {
  if (truncatedReason !== 'BUDGET_EXHAUSTED') return null;

  const safeTotal = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  const safeCompleted = Number.isFinite(completed) ? Math.max(0, Math.floor(completed)) : 0;
  const scored = Math.min(safeCompleted, safeTotal);
  const notAnalysed = Math.max(0, safeTotal - scored);
  if (notAnalysed === 0) return null;

  return lang === 'fr'
    ? `Passe arretee a ${scored} marques sur ${safeTotal} : le budget en credits est epuise. ${notAnalysed} marques n'ont pas ete analysees, la liste n'est pas complete`
    : `Pass stopped at ${scored} of ${safeTotal} brands: the credit budget is spent. ${notAnalysed} brands were not analysed, so the list is incomplete`;
};
